<template>
  <div class="sentiment-index-mini-cards" :class="{ 'site-dark-mode': theme === 'dark' }">
    <div class="row index-grid">
      <div v-for="index in indices" :key="index.code" class="col-12 col-sm-6 col-md-4 index-col-5 mb-2">
        <div class="card index-card shadow-sm border-0 h-100">
          <div class="card-body index-card-body d-flex flex-column justify-content-between">
            <!-- Header: Title and Real-time Snapshot -->
            <div class="d-flex align-items-center justify-content-between mb-1">
              <div class="d-flex align-items-center">
                <span class="mr-1 text-muted"><b-icon icon="clock" font-scale="0.9"></b-icon></span>
                <span class="font-weight-bold index-title">{{ index.display_name }}<span class="index-code text-muted small">({{ index.display_code }})</span></span>
              </div>
              <div class="text-right">
                <div class="font-weight-bold price-value" :style="{ color: getChangeColor(index.px_change_rate) }">
                  {{ formatPrice(index.last_px, index.code) }}
                </div>
                <div class="small-change d-flex align-items-center justify-content-end" :style="{ color: getChangeColor(index.px_change_rate) }">
                  <span>{{ index.px_change_rate > 0 ? '+' : '' }}{{ formatPercent(index.px_change_rate) }}%</span>
                  <span class="ml-1">{{ index.px_change > 0 ? '+' : '' }}{{ formatPrice(index.px_change, index.code) }}</span>
                </div>
              </div>
            </div>

            <!-- ECharts Minute Trend -->
            <div class="index-chart-container position-relative">
              <v-chart
                v-if="index.trend && index.trend.length > 0 && index.preclose_px"
                class="mini-index-chart"
                :options="getChartOptions(index)"
                :theme="echartsTheme"
                :key="index.code + '_' + (index.preclose_px || 0) + '_' + index.trend.length + '_' + theme"
                autoresize
              />
              <div v-else class="text-center py-4 text-muted small">
                <b-spinner small variant="secondary" class="mr-1"></b-spinner>
                数据加载中...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import { request_zzquant } from "network/request.js"

export default {
  name: 'SentimentIndexMiniCards',
  components: {
    'v-chart': ECharts
  },
  data() {
    return {
      theme: 'light',
      timer: null,
      indices: [
        { code: '000001.SS', display_code: '1A0001', display_name: '上证', last_px: null, preclose_px: null, px_change: null, px_change_rate: null, trend: [] },
        { code: '399001.SZ', display_code: '399001', display_name: '深证', last_px: null, preclose_px: null, px_change: null, px_change_rate: null, trend: [] },
        { code: '399006.SZ', display_code: '399006', display_name: '创业', last_px: null, preclose_px: null, px_change: null, px_change_rate: null, trend: [] },
        { code: '000688.SS', display_code: '000688', display_name: '科创50', last_px: null, preclose_px: null, px_change: null, px_change_rate: null, trend: [] },
        { code: '899050.BJ', display_code: '899050', display_name: '北证50', last_px: null, preclose_px: null, px_change: null, px_change_rate: null, trend: [] }
      ]
    }
  },
  computed: {
    echartsTheme() {
      return this.theme === 'dark' ? 'site-dark' : null
    }
  },
  mounted() {
    this.refresh();
    // Refresh every 30 seconds
    this.timer = setInterval(() => {
      this.refreshInterval();
    }, 30000);

    // Read initial theme and listen for changes
    try {
      const t = localStorage.getItem('site.theme');
      if (t) {
        this.theme = t;
      } else if (document.body.classList.contains('theme-dark')) {
        this.theme = 'dark';
      }
    } catch (e) {
      console.warn('Theme read failed', e);
    }
    window.addEventListener('site-theme-changed', this.onThemeChanged);
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    window.removeEventListener('site-theme-changed', this.onThemeChanged);
  },
  methods: {
    onThemeChanged(e) {
      if (e && e.detail && e.detail.theme) {
        this.theme = e.detail.theme;
      }
    },
    getChangeColor(val) {
      if (val === null || val === undefined) return '#999999';
      const num = parseFloat(val);
      if (isNaN(num)) return '#999999';
      if (num > 0) return '#ef4444'; // Red
      if (num < 0) return '#10b981'; // Green
      return '#999999'; // Grey
    },
    formatPrice(val, code) {
      if (val === null || val === undefined) return '--';
      const num = parseFloat(val);
      if (isNaN(num)) return '--';
      // BJ50 has 2 decimal digits but is usually small, SH/SZ composite are large
      if (code === '899050.BJ') {
        return num.toFixed(2);
      }
      return num.toFixed(2);
    },
    formatPercent(val) {
      if (val === null || val === undefined) return '--';
      const num = parseFloat(val);
      if (isNaN(num)) return '--';
      return num.toFixed(2);
    },
    refreshInterval() {
      const myDate = new Date();
      const day = myDate.getDay();
      const hour = myDate.getHours();
      // Only auto refresh during trading hours (9:15 to 15:30) on weekdays
      if (day > 0 && day < 6 && hour >= 9 && hour <= 15) {
        this.refresh();
      } else {
        console.log("Indices non-trading hours, skipping auto-refresh");
      }
    },
    refresh() {
      request_zzquant({
        url: 'v3/market/index/trends'
      })
      .then(resp => {
        if (resp && resp.data && resp.data.code === 200 && Array.isArray(resp.data.data)) {
          const list = resp.data.data;
          this.indices = this.indices.map(index => {
            const matched = list.find(item => item.code === index.code);
            if (matched) {
              return {
                ...index,
                last_px: matched.last_px,
                preclose_px: matched.preclose_px,
                open_px: matched.open_px,
                high_px: matched.high_px,
                low_px: matched.low_px,
                px_change: matched.px_change,
                px_change_rate: matched.px_change_rate,
                trend: Array.isArray(matched.trend) ? matched.trend : []
              };
            }
            return index;
          });
        }
      })
      .catch(err => {
        console.error("Failed to load index trend cards:", err);
      });
    },
    getChartOptions(index) {
      const isDark = this.theme === 'dark';
      const preclose = parseFloat(index.preclose_px) || 0;

      // 将价格转换为相对昨收的涨跌幅（%），0轴即为涨跌平衡线
      const times = index.trend.map(t => t.time);
      const values = index.trend.map(t => {
        const price = parseFloat(t.price);
        if (isNaN(price) || !preclose) return null;
        return parseFloat(((price - preclose) / preclose * 100).toFixed(4));
      });

      // 手动拆分为红/绿两组数据，在穿越0轴处线性插值
      // ECharts 4.x 不支持 visualMap 对折线分段着色，所以手动拆分
      var redValues = new Array(values.length).fill(null);
      var greenValues = new Array(values.length).fill(null);

      for (var i = 0; i < values.length; i++) {
        var v = values[i];
        if (v === null) continue;

        if (v >= 0) {
          redValues[i] = v;
        } else {
          greenValues[i] = v;
        }

        // 检测与前一个有效点之间是否穿越0轴
        if (i > 0 && values[i - 1] !== null && v !== null) {
          var prev = values[i - 1];
          if ((prev > 0 && v < 0) || (prev < 0 && v > 0)) {
            // 穿越0轴，两条线在此点都设为0，确保视觉连续
            redValues[i - 1] = prev >= 0 ? prev : 0;
            redValues[i] = v >= 0 ? v : 0;
            greenValues[i - 1] = prev <= 0 ? prev : 0;
            greenValues[i] = v <= 0 ? v : 0;
          }
        }
      }

      console.log(`[ECharts Options] ${index.display_name} (${index.code}) - preclose: ${preclose}, data points: ${values.length}`);

      return {
        grid: {
          top: 8,
          bottom: 20,
          left: 10,
          right: 10
        },
        tooltip: {
          show: true,
          trigger: 'axis',
          transitionDuration: 0,
          borderWidth: 0,
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          shadowColor: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
          shadowBlur: 6,
          textStyle: {
            color: isDark ? '#f8fafc' : '#333333',
            fontSize: 11
          },
          formatter: function (params) {
            if (!params || params.length === 0) return '';
            // 取第一个有有效值的 series
            var p = null;
            for (var k = 0; k < params.length; k++) {
              if (params[k].value !== null && params[k].value !== undefined && params[k].value !== '-') {
                p = params[k];
                break;
              }
            }
            if (!p) return '';
            var time = times[p.dataIndex];
            var changeRate = parseFloat(p.value);
            if (isNaN(changeRate) || !preclose) return '';

            var price = preclose * (1 + changeRate / 100);
            var color = changeRate > 0 ? '#ef4444' : (changeRate < 0 ? '#10b981' : '#999999');
            var sign = changeRate > 0 ? '+' : '';

            return '<div style="font-family: sans-serif; font-size: 11px; padding: 2px 4px; text-align: left;">' +
              '<div style="color: #64748b; margin-bottom: 2px;">时间: <strong style="color: ' + (isDark ? '#f8fafc' : '#1e293b') + ';">' + time + '</strong></div>' +
              '<div>当前价格: <strong style="color: ' + (isDark ? '#f8fafc' : '#1e293b') + ';">' + price.toFixed(2) + '</strong></div>' +
              '<div style="margin-top: 2px;">当前涨幅: <strong style="color: ' + color + ';">' + sign + changeRate.toFixed(2) + '%</strong></div>' +
            '</div>';
          }
        },
        xAxis: {
          type: 'category',
          data: times,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            show: true,
            interval: function (idx, val) {
              return val === '09:30' || val === '11:30' || val === '15:00';
            },
            color: isDark ? '#7a8291' : '#6c757d',
            fontSize: 9
          }
        },
        yAxis: {
          type: 'value',
          show: false,
          min: function (value) {
            var maxAbs = Math.max(Math.abs(value.max), Math.abs(value.min));
            var pad = (maxAbs > 0 ? maxAbs : 1) * 1.15;
            return -pad;
          },
          max: function (value) {
            var maxAbs = Math.max(Math.abs(value.max), Math.abs(value.min));
            var pad = (maxAbs > 0 ? maxAbs : 1) * 1.15;
            return pad;
          }
        },
        series: [
          {
            // 红色线（0轴以上）
            name: 'up',
            type: 'line',
            data: redValues,
            smooth: true,
            showSymbol: false,
            connectNulls: false,
            lineStyle: { color: '#ef4444', width: 1.5 },
            itemStyle: { color: '#ef4444' },
            areaStyle: {
              color: {
                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(239, 68, 68, 0.35)' },
                  { offset: 1, color: 'rgba(239, 68, 68, 0.03)' }
                ]
              }
            },
            markLine: {
              silent: true,
              symbol: ['none', 'none'],
              label: { show: false },
              lineStyle: {
                type: 'dashed',
                color: isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(100, 116, 139, 0.4)',
                width: 1
              },
              data: [{ yAxis: 0 }]
            }
          },
          {
            // 绿色线（0轴以下）
            name: 'down',
            type: 'line',
            data: greenValues,
            smooth: true,
            showSymbol: false,
            connectNulls: false,
            lineStyle: { color: '#10b981', width: 1.5 },
            itemStyle: { color: '#10b981' },
            areaStyle: {
              color: {
                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(16, 185, 129, 0.03)' },
                  { offset: 1, color: 'rgba(16, 185, 129, 0.35)' }
                ]
              }
            }
          }
        ]
      };
    }
  }
}
</script>

<style scoped>
.index-grid { margin-left: -5px; margin-right: -5px; }
.index-grid > div { padding-left: 5px; padding-right: 5px; }
.index-card {
  background-color: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.07));
  border-radius: 9px;
  transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
}

.index-card:hover {
  transform: translateY(-2px);
  border-color: rgba(2, 132, 199, 0.4) !important;
  box-shadow: 0 4px 14px rgba(30, 45, 65, .09) !important;
}

.index-card-body { padding: 9px 11px; }

.index-title {
  color: var(--text-main, #333333);
  font-size: .9rem;
}

.index-code {
  font-weight: normal;
  margin-left: 2px;
}

.price-value {
  font-size: .95rem;
  line-height: 1.2;
}

.small-change {
  font-size: .68rem;
  font-weight: 500;
  white-space: nowrap;
}

.index-chart-container {
  height: 82px;
  width: 100%;
  margin-top: 2px;
}

.mini-index-chart {
  width: 100% !important;
  height: 82px !important;
}

/* Dark Mode styles mapping */
body.theme-dark .index-card,
.theme-dark .index-card,
.site-dark-mode .index-card {
  background-color: var(--bg-card, #202734) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;
}

body.theme-dark .index-card:hover,
.theme-dark .index-card:hover,
.site-dark-mode .index-card:hover {
  border-color: rgba(96, 165, 250, 0.45) !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4) !important;
}

body.theme-dark .index-title,
.theme-dark .index-title,
.site-dark-mode .index-title {
  color: var(--text-main, #f8fafc) !important;
}

@media (min-width: 1200px) {
  .index-col-5 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
